import React, { useEffect, useRef, useState, useMemo } from "react";
import DashboardContent from "../components/DashboardContent";
import { ReactComponent as DeleteIcon } from "../assets/images/icons/bin-black.svg";
import { ReactComponent as DownloadIcon } from "../assets/images/icons/download.svg";
import { ReactComponent as ArrowRightIcon } from "../assets/images/icons/arrow-right.svg";
import { ReactComponent as CloseIcon } from "../assets/images/icons/close.svg";
import { CsrAPI } from "../apis/CsrAPI";
import { useNavigate, Link } from "react-router-dom";
import useCsr from "../hooks/useCsr";
import { useSession } from "../contexts/SessionContext";
// import { useAuth0 } from "@auth0/auth0-react";
import Loader from "../utils/Loader";
import * as Sentry from "@sentry/react";
import { CSR_STATUS } from "../utils/Constants";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

ModuleRegistry.registerModules([AllCommunityModule]);

// Only use Sentry in production environment
const isProduction = process.env.NODE_ENV === "production";
import RedirectHandler from "../components/RedirectHandler";

const Dashboard = () => {
  const {
    setAjaxLoading,
    setCurrentStudyName,
    setCurrentLaySummaryName,
    setCurrentRequestId,
    setCurrentRequestTime,
    downloadLatestFile,
    wrongEnvironment,
    setCurrentRequestIdOfLaySummary,
    setMainStatusOfLaySummary,
    setCurrentRequestTimeOfLaySummary,
    currentStudyName,
    currentRequestId,
    currentRequestTime,
    currentLaySummaryName,
    currentRequestIdOfLaySummary,
    mainStatusOfLaySummary,
    currentRequestTimeOfLaySummary,
    setMainStatus,
    mainStatus,
    setSelectedStudy,
  } = useCsr();

  const navigate = useNavigate();
  const { sessionReady } = useSession();
  // const { user } = useAuth0();

  const [csrList, setCsrList] = useState([]);

  const errRef = useRef();
  const [errMsg, setErrMsg] = useState("");

  const [showRedirect, setShowRedirect] = useState(false);

  const fetchCsrList = async () => {
    setAjaxLoading(true);
    try {
      const fetchedCsrList = await CsrAPI.getAllStudies();
      setCsrList(fetchedCsrList);
      setErrMsg("");
    } catch (error) {
      if (isProduction) {
        Sentry.captureException(error);
      }
      if (!error?.response) {
        setErrMsg("No Server Response");
      } else if (error.response?.status === 401) {
        setErrMsg("Invalid user credentials");
      } else if (error.response?.status === 403) {
        setShowRedirect(true);
      } else {
        setErrMsg("Fail to get the list of studies");
      }
      if (errRef.current) {
        errRef.current?.focus();
      }
    } finally {
      setAjaxLoading(false);
    }
  };

  useEffect(() => {
    if (sessionReady) {
      setAjaxLoading(true);

      //remove all current sessions
      setCurrentStudyName("");
      setSelectedStudy("");
      setCurrentLaySummaryName("");
      setCurrentRequestId("");
      setCurrentRequestTime("");
      setCurrentRequestIdOfLaySummary("");
      setMainStatusOfLaySummary(CSR_STATUS.inProgress);
      setMainStatus(CSR_STATUS.inProgress);
      setCurrentRequestTimeOfLaySummary("");
      setErrMsg("");

      fetchCsrList();
    }
  }, [
    sessionReady,
    currentStudyName,
    currentRequestId,
    currentRequestTime,
    currentLaySummaryName,
    currentRequestIdOfLaySummary,
    mainStatusOfLaySummary,
    currentRequestTimeOfLaySummary,
    mainStatus,
  ]);

  const handleEdit = (e, studyName, studyId, studyType, requestStatus) => {
    e.stopPropagation();

    if (studyType === "CSR") {
      if (requestStatus === "render available") {
        setMainStatus(CSR_STATUS.ready);
      }
      setCurrentStudyName(studyName);
      navigate("/csr/" + studyId + "/summary");
    } else if (studyType === "SCS") {
      setCurrentLaySummaryName(studyName);
      navigate("/scs/" + studyId + "/summary");
    } else {
      setCurrentLaySummaryName(studyName);
      navigate("/ls/" + studyId + "/select-study");
    }
  };

  const handleDeleteStudy = async (e, id, study_type) => {
    e.stopPropagation();

    try {
      if (study_type === "CSR") {
        await CsrAPI.deleteStudy(id);
      } else if (study_type === "SCS") {
        await CsrAPI.deleteSafetySummary(id);
      } else {
        await CsrAPI.deleteLaySummary(id);
      }
      await fetchCsrList();
    } catch (error) {
      console.error("Error deleting study:", error);
      if (isProduction) {
        Sentry.captureException(error);
      }
    }
  };

  const handleDownloadLatestCsr = async (studyId, studyName) => {
    const fileURL = "/studies/" + studyId + "/csr/download_url";
    downloadLatestFile(studyId, studyName, fileURL);
  };

  const handleDownloadLatestScs = async (summaryId, summaryName) => {
    const fileURL =
      "/clinical_summaries/safety_summary/" +
      summaryId +
      "/render/download_url";
    downloadLatestFile(summaryId, summaryName, fileURL);
  };

  const PageContent = () => {
    const gridRef = useRef(null);
    const [searchFilter, setSearchFilter] = useState("");
    const [typeFilter, setTypeFilter] = useState("all"); // "all", "CSR", "LS"

    // Filter data based on search and type
    const filteredData = useMemo(() => {
      return csrList.filter(item => {
        // Type filter
        const studyType = item.study_type || "LS";
        if (typeFilter !== "all" && studyType !== typeFilter) {
          return false;
        }

        // Search filter
        if (!searchFilter.trim()) {
          return true;
        }

        const searchLower = searchFilter.toLowerCase();
        const documentName = (item.name || "").toLowerCase();

        return documentName.includes(searchLower);
      });
    }, [csrList, searchFilter, typeFilter]);

    // Date formatter for DD.MM.YYYY format
    const formatDate = dateString => {
      if (!dateString) return "";
      try {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
      } catch (e) {
        return "";
      }
    };

    // Column definitions
    const columnDefs = useMemo(
      () => [
        {
          field: "documentName",
          headerName: "Document name",
          sortable: false,
          filter: false,
          flex: 1,
          valueGetter: params => {
            return params.data.name || "";
          },
          cellRenderer: params => {
            const data = params.data;
            const studyType = data.study_type || "LS";
            const documentName = data.name || "";
            return (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}>
                <div
                  className={"dashboard-content__item-type"}
                  style={{
                    fontSize: "12px",
                    lineHeight: "12px",
                    fontWeight: 500,
                    textTransform: "uppercase",
                    backgroundColor:
                      studyType === "CSR"
                        ? "#4AB8AA"
                        : studyType === "SCS"
                          ? "#C2410C"
                          : "#070FED",
                    color: "#fff",
                    padding: "3px 6px",
                    borderRadius: "6px",
                    flexShrink: 0,
                    display: "flex",
                    width: "44px",
                    height: "24px",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "10px",
                  }}>
                  {studyType}
                </div>
                <div
                  className={"dashboard-content__item-name"}
                  style={{
                    paddingLeft: "12px",
                    fontSize: "14px",
                    lineHeight: "19.6px",
                    fontWeight: 500,
                  }}>
                  {documentName}
                </div>
              </div>
            );
          },
        },
        {
          field: "dateCreated",
          headerName: "Date created",
          sortable: true,
          width: 250,
          valueGetter: params => {
            return params.data.created_at
              ? new Date(params.data.created_at)
              : null;
          },
          valueFormatter: params => {
            return formatDate(params.value);
          },
        },
        {
          field: "lastGenerated",
          headerName: "Last modified",
          sortable: true,
          width: 250,
          sort: "desc",
          valueGetter: params => {
            return params.data.modified_at
              ? new Date(params.data.modified_at)
              : null;
          },
          valueFormatter: params => {
            return formatDate(params.value);
          },
        },
        {
          field: "status",
          headerName: "Status",
          sortable: false,
          filter: false,
          width: 250,
          valueGetter: params => {
            const status = params.data.latest_request_status;
            // Statuses that map to "Ready for download"
            const readyForDownloadStatuses = [
              "COMPLETED",
              "render available",
              "finished",
            ];
            const normalizedStatus = status ? String(status).trim() : "";
            const normalizedReadyStatuses = readyForDownloadStatuses.map(s =>
              String(s).toLowerCase()
            );
            const isFinished =
              normalizedStatus &&
              normalizedReadyStatuses.includes(normalizedStatus.toLowerCase());
            return isFinished ? "Ready for download" : "In progress";
          },
          cellRenderer: params => {
            const data = params.data;
            const status = data.latest_request_status;

            // Statuses that map to "Ready for download"
            const readyForDownloadStatuses = [
              "COMPLETED",
              "COMPLETED_WITH_ERRORS",
              "render available",
              "finished",
            ];

            // Normalize status for comparison (case-insensitive)
            // All other statuses (CREATED, QUEUED, PENDING, STOPPED, CANCELED,
            // AI_SUMMARY_COMPLETED, ready, error, stopped) map to "In progress"
            const normalizedStatus = status ? String(status).trim() : "";
            const normalizedReadyStatuses = readyForDownloadStatuses.map(s =>
              String(s).toLowerCase()
            );
            const isFinished =
              normalizedStatus &&
              normalizedReadyStatuses.includes(normalizedStatus.toLowerCase());
            const statusText = isFinished ? "Ready for download" : "Active";

            return (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  justifyContent: "space-between",
                  width: "200px",
                }}>
                <span style={{ fontSize: "14px", lineHeight: "19.6px" }}>
                  {statusText}
                </span>
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    marginLeft: "auto",
                  }}
                  data-action-buttons="true"
                  onClick={e => e.stopPropagation()}
                  onMouseDown={e => e.stopPropagation()}>
                  {isFinished && (
                    <div
                      style={{ cursor: "pointer", lineHeight: 0 }}
                      title="Download"
                      data-action="download"
                      onClick={e => {
                        e.stopPropagation();
                        if (data.study_type === "LS") {
                          const id = data.id;
                          const fileURL =
                            "/lay-summaries/" +
                            id +
                            "/requests/" +
                            data.latest_request_id +
                            "/documents?document_type='lay-summary'";

                          downloadLatestFile(
                            data.id,
                            data.name + "_LaySummary.docx",
                            fileURL
                          );
                        } else if (data.study_type === "SCS") {
                          handleDownloadLatestScs(data.id, data.name + ".docx");
                        } else {
                          handleDownloadLatestCsr(data.id, data.name + ".docx");
                        }
                      }}>
                      <DownloadIcon />
                    </div>
                  )}
                  <div
                    style={{ cursor: "pointer", lineHeight: 0 }}
                    data-action="delete"
                    title="Delete"
                    onClick={e => {
                      e.stopPropagation();

                      handleDeleteStudy(e, data.id, data.study_type);
                    }}>
                    <DeleteIcon />
                  </div>
                </div>
              </div>
            );
          },
        },
      ],
      []
    );

    const defaultColDef = useMemo(
      () => ({
        resizable: true,
        sortable: true,
        filter: false,
      }),
      []
    );

    const onRowClicked = params => {
      // Check if the click originated from an action button
      const target = params.event.target;
      const isActionButton =
        target.closest("[data-action-buttons]") ||
        target.closest("[data-action]");

      if (isActionButton) {
        return; // Don't trigger edit if clicking on action buttons
      }

      const data = params.data;
      handleEdit(
        params.event,
        data.name,
        data.id,
        data.study_type,
        data.latest_request_status
      );
    };

    return (
      <>
        <span
          ref={errRef}
          className={errMsg ? "errmsg" : "offscreen"}
          aria-live="assertive">
          {errMsg}
        </span>
        <div className="welcome-section">
          <div className="welcome-header">
            {/* <h1 className="welcome-title">Welcome {user?.name || "User"}</h1> */}
            {/* <div className="welcome-divider"></div> */}
          </div>
          <div className="welcome-cards">
            <div className="welcome-card">
              <h2 className="welcome-card-title">Clinical Study Report</h2>
              <p className="welcome-card-description">
                Generate a Clinical Study Report by uploading study data,
                protocols, and related files.
              </p>
              <ul className="welcome-card-features">
                <li>
                  <span className="feature-icon"></span>
                  <span>Signal identification</span>
                </li>
                <li>
                  <span className="feature-icon"></span>
                  <span>Rapid data processing</span>
                </li>
                <li>
                  <span className="feature-icon"></span>
                  <span>Compliant document generation</span>
                </li>
                <li>
                  <span className="feature-icon"></span>
                  <span>Built on our deterministic AI</span>
                </li>
              </ul>
              <Link to="/csr/new/summary" className="welcome-card-button">
                Create new CSR document
                <ArrowRightIcon />
              </Link>
            </div>
            <div className="welcome-card">
              <h2 className="welcome-card-title">Lay Summary</h2>
              <p className="welcome-card-description">
                Turn your study into an easy-to-understand summary for patients
                and the public.
              </p>
              <ul className="welcome-card-features">
                <li>
                  <span className="feature-icon"></span>
                  <span>
                    Built with specialized LLM-powered medical writing agents
                  </span>
                </li>
                <li>
                  <span className="feature-icon"></span>
                  <span>
                    Converts complex clinical data into patient-friendly
                    summaries
                  </span>
                </li>
                <li>
                  <span className="feature-icon"></span>
                  <span>
                    Includes all 10 regulatory components required by CTEG
                  </span>
                </li>
                <li>
                  <span className="feature-icon"></span>
                  <span>
                    Accessible lay summaries that meet both regulatory standards
                    and patient needs
                  </span>
                </li>
              </ul>
              <Link to="/ls/new/select-study" className="welcome-card-button">
                Create new lay summary
                <ArrowRightIcon />
              </Link>
            </div>
            <div className="welcome-card">
              <h2 className="welcome-card-title">
                Module 2.7.3: Summary of Clinical Efficacy
              </h2>
              <p className="welcome-card-description">
                Turn your study into an easy-to-understand summary for patients
                and the public.
              </p>
              <ul className="welcome-card-features">
                <li>
                  <span className="feature-icon"></span>
                  <span>
                    Built with specialized LLM-powered medical writing agents
                  </span>
                </li>
                <li>
                  <span className="feature-icon"></span>
                  <span>
                    Converts complex clinical data into patient-friendly
                    summaries
                  </span>
                </li>
                <li>
                  <span className="feature-icon"></span>
                  <span>
                    Includes all 10 regulatory components required by CTEG
                  </span>
                </li>
                {/* <li>
                  <span className="feature-icon"></span>
                  <span>
                    Accessible lay summaries that meet both regulatory standards
                    and patient needs
                  </span>
                </li> */}
              </ul>
              <Link to="/ls/new/select-study" className="welcome-card-button">
                Create new Summary of Clinical Efficacy
                <ArrowRightIcon />
              </Link>
            </div>
            <div className="welcome-card">
              <h2 className="welcome-card-title">
                Module 2.7.4: Summary of Clinical Safety
              </h2>
              <p className="welcome-card-description">
                Turn your study into an easy-to-understand summary for patients
                and the public.
              </p>
              <ul className="welcome-card-features">
                <li>
                  <span className="feature-icon"></span>
                  <span>
                    Deterministically surfaces the clinically relevant safety
                    signals in your data — not just a summary
                  </span>
                </li>
                <li>
                  <span className="feature-icon"></span>
                  <span>
                    Every signal traces back to its source record, built to hold
                    up at the validation table
                  </span>
                </li>
                <li>
                  <span className="feature-icon"></span>
                  <span>
                    Rules-based and fully auditable: the same data always
                    produces the same output
                  </span>
                </li>
                {/* <li>
                  <span className="feature-icon"></span>
                  <span>
                    Accessible lay summaries that meet both regulatory standards
                    and patient needs
                  </span>
                </li> */}
              </ul>
              <Link to="/scs/new/summary" className="welcome-card-button">
                Create new Summary of Clinical Safety
                <ArrowRightIcon />
              </Link>
            </div>
          </div>
        </div>
        <div className={"dashboard-content__items"}>
          <style>{`
            .welcome-section {
              padding: 0 40px 0px 40px;
            }
            .welcome-header {
              margin-bottom: 40px;
              color: var(--Neutral-grey-900, #111827);
              font-family: "Public Sans";
              font-size: 32px;
              font-style: normal;
              font-weight: 600;
              line-height: 38px; 
              letter-spacing: -1px;
              padding-top: 24px;
            }
            .welcome-title {
              // font-family: "Public Sans", sans-serif;
              // font-size: 32px;
              // font-weight: 600;
              // line-height: 120%;
              // color: #111827;
              // margin: 0 0 16px 0;

    
            }
            .welcome-divider {
              height: 1px;
              background-color: #dce0e5;
              width: 100%;
            }
            .welcome-cards {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 24px;
              margin-bottom: 40px;
            }
            .welcome-card {
              background-color: #fff;
              border: 1px solid #dce0e5;
              border-radius: 2px;
              padding: 32px;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
              transition: all 0.3s ease;
              border-top: 2px solid #4AB8AA;
            }
            .welcome-card:hover {
              border-top: 4px solid #4AB8AA;
            }
            .welcome-card-title {
              font-family: "Public Sans", sans-serif;
              font-size: 20px;
              font-weight: 600;
              line-height: 120%;
              color: #111827;
              margin: 0 0 12px 0;
            }
            .welcome-card-description {
              font-family: "Public Sans", sans-serif;
              font-size: 14px;
              font-weight: 400;
              line-height: 20px;
              color: #6b7280;
              margin: 0 0 24px 0;
            }
            .welcome-card-features {
              list-style: none;
              padding: 0;
              margin: 0 0 32px 0;
            }
            .welcome-card-features li {
              display: flex;
              align-items: center;
              gap: 12px;
              margin-bottom: 12px;
              font-family: "Public Sans", sans-serif;
              font-size: 14px;
              color: #111827;
            }
            .welcome-card-features li:last-child {
              margin-bottom: 0;
            }
            .feature-icon {
              width: 8px;
              height: 8px;
              background-color: #4ab8aa;
              border-radius: 50%;
              flex-shrink: 0;
            }
            .welcome-card-button {
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
              width: 100%;
              padding: 12px 24px;
              background-color: #474eff;
              color: #fff;
              border: none;
              border-radius: 8px;
              font-family: "Public Sans", sans-serif;
              font-size: 16px;
              font-weight: 600;
              text-decoration: none;
              cursor: pointer;
              transition: background-color 0.2s;
            }
            .welcome-card:hover .welcome-card-button {
              background-color: #173261;
            }
            .welcome-card-button svg {
              width: 16px;
              height: 16px;
            }
            .welcome-card-button svg path {
              fill: #fff;
            }
            .ag-theme-alpine .ag-row:hover {
              background-color: #f0f3f4;
              cursor: pointer;
            }
            .ag-theme-alpine .ag-header-cell {
              padding: 12px;
            }
            .ag-theme-alpine .ag-cell {
              padding: 12px;
              display: flex;
              align-items: center;
            }
            .ag-theme-alpine .ag-root-wrapper,
            .ag-theme-alpine .ag-root {
              border: none !important;
            }
              .documents-container {
              gap: 12px;
              align-items: center;
              padding: 0 40px 0px 40px;
              
            }
              .document-title-container{
              padding-left: 10px;
              }
              .document-title {
                color: var(--Neutral-grey-900, #111827);
                font-family: "Public Sans";
                font-size: 20px;
                font-style: normal;
                font-weight: 600;
                line-height: 120%;
                letter-spacing: -0.25px;
                padding-top: 24px;
                border-top: 2px solid #4AB8AA;
              }
              .dashboard-content__items {
            .dashboard-filter-controls {
              display: flex;
              gap: 12px;
              align-items: center;
              padding: 24px 0px 20px 0px;
              justify-content: space-between;
            }
            .dashboard-filter-buttons {
              display: flex;
              gap: 8px;
              padding-left: 10px;
            }
              .filter-button-group {
                display: flex;
                gap: 8px;
              }
            .dashboard-filter-button {
              padding: 8px 24px;
              border: 1px solid #dce0e5;
              border-radius: var(--radius-round, 999px);
              background-color: #fff;
              color: #111827;
              font-size: 14px;
              font-weight: 500;
              cursor: pointer;
              transition: background-color 0.2s;
            }
            .dashboard-filter-button:hover {
              background-color: #f0f3f4;
            }
            .dashboard-filter-button.active {
              background-color: #474eff;
              color: #fff;
              border-color: #474eff;
            }
            .dashboard-search-input {
              width: 100%;
              padding: 8px 16px;
              border: 1px solid #dce0e5;
              border-radius: 8px;
              font-size: 14px;
              font-weight: 500;
            }
            .dashboard-search-input:focus {
              outline: none;
              border-color: #005aa7;
              box-shadow: 0 0 6px rgba(35, 44, 67, 0.25);
            }
              .ag-header-cell-text{
              color: var(--Neutral-grey-900, #111827);
              font-family: "Public Sans";
              font-size: 16px;
              font-style: normal;
              font-weight: 600;
              line-height: 22px; 
              }
          `}</style>
          <div className="documents-container">
            <div className="document-title-container">
              <div className="document-title">Your documents</div>
              <div>Manage your clinical study reports and lay summaries </div>
            </div>
            <div className="dashboard-filter-controls">
              <div className="dashboard-filter-buttons">
                <div className="filter-button-group">
                  <button
                    className={`dashboard-filter-button ${typeFilter === "all" ? "active" : ""}`}
                    onClick={() => setTypeFilter("all")}>
                    View all
                  </button>
                  <button
                    className={`dashboard-filter-button ${typeFilter === "CSR" ? "active" : ""}`}
                    onClick={() => setTypeFilter("CSR")}>
                    Clinical study reports
                  </button>
                  <button
                    className={`dashboard-filter-button ${typeFilter === "LS" ? "active" : ""}`}
                    onClick={() => setTypeFilter("LS")}>
                    Lay summaries
                  </button>
                  <button
                    className={`dashboard-filter-button ${typeFilter === "SCS" ? "active" : ""}`}
                    onClick={() => setTypeFilter("SCS")}>
                    Summary of clinical safety
                  </button>
                </div>
              </div>

              <div style={{ position: "relative", flex: 1, maxWidth: "300px" }}>
                <input
                  type="text"
                  className="dashboard-search-input"
                  placeholder="Search"
                  value={searchFilter}
                  onChange={e => setSearchFilter(e.target.value)}
                  style={{ paddingRight: searchFilter ? "36px" : "16px" }}
                />
                {searchFilter && (
                  <div
                    onClick={() => setSearchFilter("")}
                    style={{
                      position: "absolute",
                      right: "8px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      backgroundColor: "transparent",
                      transition: "background-color 0.2s",
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.backgroundColor = "#4AB8AA";
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.backgroundColor = "#4AB8AA";
                    }}>
                    <CloseIcon
                      style={{
                        width: "12px",
                        height: "12px",
                        fill: "#111827",
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div
            className="ag-theme-alpine"
            style={{
              height: "600px",
              width: "100%",
              padding: "0px 40px 20px 40px",
            }}>
            <AgGridReact
              theme="legacy"
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              onRowClicked={onRowClicked}
              rowHeight={60}
              headerHeight={55}
              animateRows={true}
              suppressRowClickSelection={true}
              getRowId={params => params.data.id}
            />
          </div>
        </div>
      </>
    );
  };

  if (!sessionReady) {
    return <Loader />;
  }

  if (showRedirect || wrongEnvironment) {
    return <RedirectHandler />;
  }

  return (
    <DashboardContent
      sidebarCSR={false}
      headerTitle={""}
      headerButton={false}
      pageContent={PageContent()}
    />
  );
};

export default Dashboard;
